import { FrameBlockModel, GroupElementModel } from '@blocksuite/affine-model';
import {
  Bound,
  deserializeXYWH,
  getCommonBound,
  getQuadBoundWithRotation,
} from '@blocksuite/global/gfx';
import type {
  GfxBlockElementModel,
  GfxController,
  GfxModel,
} from '@blocksuite/std/gfx';
import type { BlockModel } from '@blocksuite/store';

import {
  EDGELESS_FOCUS_MAX_ZOOM,
  edgelessFocusPadding,
} from '../consts/edgeless-handle.js';

export function getRenderXYWH(selectable: GfxModel) {
  return selectable.transientXYWH ?? selectable.xywh;
}

type FitToScreenElement = {
  elementBound: Bound;
};

type FitToScreenView = {
  getRenderingRect: () => {
    h: number;
    w: number;
    x: number;
    y: number;
  };
};

function exposesModelCoordinateRenderingRect(
  view: unknown
): view is FitToScreenView {
  return (
    typeof view === 'object' &&
    view !== null &&
    'getRenderingRect' in view &&
    typeof view.getRenderingRect === 'function'
  );
}

/** Resolve the canonical model-coordinate geometry used by Fit to screen. */
export function resolveFitToScreenBound(
  element: FitToScreenElement,
  view: unknown
): Bound {
  if (exposesModelCoordinateRenderingRect(view)) {
    return Bound.from(view.getRenderingRect());
  }
  return element.elementBound;
}

/** The app's open AI panel over the view's right side: its width plus its inset, published on the view. */
export const AI_PANEL_INLINE_END_PROPERTY = '--streamlain-ai-panel-inline-end';

/** Width of the view's right side covered by the app's open AI panel; 0 while it is closed. */
export function aiPanelInlineEnd(element: Element) {
  return (
    parseFloat(
      getComputedStyle(element).getPropertyValue(AI_PANEL_INLINE_END_PROPERTY)
    ) || 0
  );
}

/**
 * Brings elements into focus: fits them into the view clear of its docked overlays (minimap, dock,
 * the open AI panel) with room for the drag handle above them, at most EDGELESS_FOCUS_MAX_ZOOM. When
 * the elements fit at the same zoom beside the minimap with equal room on both sides, they stand on
 * the view's axis. Returns false when no element has an area.
 */
export function focusElementsOnScreen(
  gfx: GfxController,
  elements: readonly GfxModel[]
) {
  const bounds = elements
    .map(element => resolveFitToScreenBound(element, gfx.view.get(element)))
    .filter(bound => bound.w > 0 && bound.h > 0);
  const bound = getCommonBound(bounds);
  if (!bound) return false;
  const { viewport } = gfx;
  const [top, right, bottom, left] = viewport.overlayInsets;
  const aiPanel = viewport.element ? aiPanelInlineEnd(viewport.element) : 0;
  const padding = edgelessFocusPadding([
    top,
    Math.max(right, aiPanel),
    bottom,
    left,
  ]);
  const fit = viewport.getFitToScreenData(
    bound,
    padding,
    EDGELESS_FOCUS_MAX_ZOOM,
    0
  );
  // Equal room on both sides around the free view's axis, kept when it costs no zoom.
  const side = Math.max(padding[1] - aiPanel, padding[3]);
  const axis = viewport.getFitToScreenData(
    bound,
    [padding[0], side + aiPanel, padding[2], side],
    EDGELESS_FOCUS_MAX_ZOOM,
    0
  );
  const { zoom, centerX, centerY } = axis.zoom >= fit.zoom ? axis : fit;
  viewport.setViewport(zoom, [centerX, centerY], true);
  return true;
}

export function getSelectedRect(selected: GfxModel[]): DOMRect {
  if (selected.length === 0) {
    return new DOMRect();
  }

  // The drawn rectangle: members still loading join it once they arrive.
  const lockedElementsByFrame = selected.flatMap(selectable => {
    if (selectable instanceof FrameBlockModel && selectable.isLocked()) {
      return selectable.loadedDescendantElements;
    }
    return [];
  });

  selected = [...new Set([...selected, ...lockedElementsByFrame])];

  if (selected.length === 1) {
    const [x, y, w, h] = deserializeXYWH(getRenderXYWH(selected[0]));
    return new DOMRect(x, y, w, h);
  }

  return elementsWithoutGroup(selected, 'loaded').reduce(
    (bounds, selectable, index) => {
      const rotate = isTopLevelBlock(selectable) ? 0 : selectable.rotate;
      const [x, y, w, h] = deserializeXYWH(getRenderXYWH(selectable));
      let { left, top, right, bottom } = getQuadBoundWithRotation({
        x,
        y,
        w,
        h,
        rotate,
      });

      if (index !== 0) {
        left = Math.min(left, bounds.left);
        top = Math.min(top, bounds.top);
        right = Math.max(right, bounds.right);
        bottom = Math.max(bottom, bounds.bottom);
      }

      bounds.x = left;
      bounds.y = top;
      bounds.width = right - left;
      bounds.height = bottom - top;

      return bounds;
    },
    new DOMRect()
  );
}

export function getElementsWithoutGroup(elements: GfxModel[]) {
  return elementsWithoutGroup(elements, 'all');
}

function elementsWithoutGroup(elements: GfxModel[], members: 'all' | 'loaded') {
  const set = new Set<GfxModel>();

  elements.forEach(element => {
    if (element instanceof GroupElementModel) {
      (members === 'all'
        ? element.descendantElements
        : element.loadedDescendantElements
      )
        .filter(descendant => !(descendant instanceof GroupElementModel))
        .forEach(descendant => set.add(descendant));
    } else {
      set.add(element);
    }
  });
  return Array.from(set);
}

export function isTopLevelBlock(
  selectable: BlockModel | GfxModel | null
): selectable is GfxBlockElementModel {
  return !!selectable && 'flavour' in selectable;
}
